/**
 * Build resource type tree from flat list
 * Returns roots + lookup map
 */
export const buildResourceTypeTree = (resourceTypes) => {
  const map = {};
  resourceTypes.forEach(rt => { map[rt.name] = { ...rt, children: [] }; });
  const roots = [];
  resourceTypes.forEach(rt => {
    if (rt.parentTypeId) {
      const parent = Object.values(map).find(r => r.id === rt.parentTypeId);
      if (parent) parent.children.push(map[rt.name]);
    } else {
      roots.push(map[rt.name]);
    }
  });
  return { roots, map };
};

/**
 * Given dimensions array like ['location','kpi'],
 * returns ordered resource type info for scope selection
 */
export const getDimensionChain = (dimensions, resourceTypeMap) => {
  return dimensions.map(dim => resourceTypeMap[dim]).filter(Boolean);
};

/**
 * Get structural hierarchy chain starting from rootType
 */
export const getStructuralChain = (rootTypeName, resourceTypeMap) => {
  const chain = [];
  let current = resourceTypeMap[rootTypeName];
  while (current) {
    chain.push(current);
    const child = Object.values(resourceTypeMap).find(
      rt => rt.parentTypeId === current.id && rt.relationType === 'STRUCTURAL'
    );
    current = child;
  }
  return chain;
};

/**
 * Format scope object for display
 */
export const formatScope = (scope) => {
  if (!scope || Object.keys(scope).length === 0) return 'Global';
  return Object.entries(scope)
    .map(([k, v]) => {
      const val = Array.isArray(v) ? v.join(', ') : v;
      return `${k}: ${val}`;
    })
    .join(' | ');
};

/**
 * Group capabilities by moduleKey
 */
export const groupCapabilitiesByModule = (capabilities) => {
  const groups = {};
  capabilities.forEach(cap => {
    const key = cap.moduleKey || 'General';
    if (!groups[key]) groups[key] = [];
    groups[key].push(cap);
  });
  return groups;
};

/**
 * Group capabilities by moduleKey and targetResourceType
 */
export const groupCapabilities = (capabilities) => {
  const groups = {};

  capabilities.forEach(cap => {
    const mod = cap.moduleKey || 'General';
    const rt = cap.targetResourceType || 'Other';

    if (!groups[mod]) groups[mod] = {};
    if (!groups[mod][rt]) groups[mod][rt] = [];

    groups[mod][rt].push(cap);
  });

  return groups;
};

/**
 * Get initials from name
 */
export const getInitials = (name = '') => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

/**
 * Debounce
 */
export const debounce = (fn, ms) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};